import { AutoAwesomeMotion, Delete, Edit } from '@mui/icons-material';
import { Avatar, Box, Button, Divider, Popover, Typography } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import format from 'date-fns/format';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Shift, Worker } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { deleteDoc } from '../../utils/api';
import currency from '../../utils/currency';

import Confirmation from '../Confirmation';

type ShiftProps = {
  shift: DbDocument<Shift>;
  start: number;
  end: number;
  onShiftEdit: (shift: DbDocument<Shift>) => void;
  onDuplicate: (shift: DbDocument<Shift>) => void;
};

const ShiftItem = ({ shift, start, end, onShiftEdit, onDuplicate }: ShiftProps) => {
  const { cafe, cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { worker } = useCafe();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canEdit =
    (shift.workerId === worker?.id && worker.credentials.edit_own_work_shifts) ||
    (shift.workerId !== worker?.id && worker?.credentials.edit_work_shifts_of_other_employees);

  const diffString = useMemo(() => {
    const diff = differenceInMinutes(shift.end, shift.start);
    const diffHoursString = Math.floor(diff / 60);
    const diffMinutesString = Math.floor(diff % 60);
    return `${diffHoursString} ${t.h[lang]} ${diffMinutesString ? `${diffMinutesString} ${t.m[lang]}` : ''}`;
  }, [lang, shift.end, shift.start]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleEdit = useCallback(() => {
    onShiftEdit(shift);
    handleClose();
  }, [onShiftEdit, shift, handleClose]);

  const handleDuplicate = useCallback(() => {
    onDuplicate(shift);
    handleClose();
  }, [onDuplicate, shift, handleClose]);

  const handleShiftClick = useCallback(() => {
    setOpen(true);
  }, []);

  const onConfirm = useCallback(async () => {
    setOpen(true);
    await deleteDoc(`cafes/${cafeSlug}/shifts/${shift.id}`);
    setConfirmOpen(false);
  }, [cafeSlug, shift.id]);

  return (
    <>
      <Box
        ref={ref}
        onClick={handleShiftClick}
        sx={{
          borderRadius: 0.5,
          position: 'absolute',
          top: 0,
          left: `${((shift.start - start) * 100) / (end - start)}%`,
          width: `${((shift.end - shift.start) * 100) / (end - start)}%`,
          height: 40,
          cursor: 'pointer',
          bgcolor: (t) => t.palette.info.main,
          ':hover': {
            bgcolor: (t) => t.palette.info.dark,
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box display="flex" flexDirection="column" p={2} maxWidth={300}>
          <Typography variant="h6" sx={{ mb: 1 }}>{`${format(shift.start, 'HH:mm')} - ${format(
            shift.end,
            'HH:mm'
          )}`}</Typography>
          <Typography>{`${t.shift_duration[lang]}: ${diffString}`}</Typography>
          {!!shift.ratePerHour && (
            <Typography>{`${t.rate_per_hour[lang]}: ${currency(shift.ratePerHour, cafe?.currency)}`}</Typography>
          )}
          {!!shift.percentageOfSales && (
            <Typography>{`${t.percentage_of_sales[lang]}: ${shift.percentageOfSales}%`}</Typography>
          )}
          {!!shift.bonus && (
            <Typography>{`${t.award_fine[lang]}: ${currency(shift.bonus, cafe?.currency)}`}</Typography>
          )}
          {!!shift.comment && <Typography>{`${t.comment[lang]}: ${shift.comment}`}</Typography>}

          {canEdit && (
            <>
              <Divider sx={{ mt: 2, mb: 1 }} />
              <Button startIcon={<AutoAwesomeMotion />} onClick={handleDuplicate} color="secondary">
                {t.duplicate[lang]}
              </Button>
              <Button startIcon={<Edit />} onClick={handleEdit} color="secondary" disabled={!!shift.paid}>
                {t.edit[lang]}
              </Button>
              <Button startIcon={<Delete />} onClick={() => setConfirmOpen(true)} color="error" disabled={!!shift.paid}>
                {t.remove[lang]}
              </Button>
            </>
          )}
        </Box>
      </Popover>

      <Confirmation
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t.remove_shift_confirm[lang]}
        onSubmit={onConfirm}
      />
    </>
  );
};

type Props = {
  worker: DbDocument<Worker>;
  shifts: DbDocument<Shift>[];
  start: number;
  end: number;
  dates: Date[];
  onDayClick: (date: Date, workerId: string) => void;
  onShiftEdit: (shift: DbDocument<Shift>) => void;
  onDuplicate: (shift: DbDocument<Shift>) => void;
};

const ShiftLine = ({ start, end, shifts, worker, dates, onDayClick, onShiftEdit, onDuplicate }: Props) => {
  const todayDateString = useMemo(() => format(new Date(), 'dd.MM.yyyy'), []);

  return (
    <Box
      display="flex"
      p={1}
      sx={{
        borderRadius: 1,
        ':hover': {
          bgcolor: (t) => `${t.palette.primary.light}11`,
        },
      }}
    >
      <Box display="flex" alignItems="center" pr={1}>
        <Avatar src={worker.photoUrl || ''} />
        <Typography sx={{ ml: 1 }} width={120} noWrap>
          {worker.name || ''}
        </Typography>
      </Box>
      <Box width="100%" position="relative">
        {dates.map((date) => {
          const timeStart = date.getTime();
          const timeEnd = timeStart + 86400000;
          const dayIndex = date.getDay();
          const isToday = format(date, 'dd.MM.yyyy') === todayDateString;

          return (
            <Box
              key={timeStart}
              onClick={() => onDayClick(date, worker.id)}
              sx={{
                position: 'absolute',
                top: -8,
                left: `${((timeStart - start) * 100) / (end - start)}%`,
                width: `${((timeEnd - timeStart) * 100) / (end - start)}%`,
                height: 56,
                borderLeft: (t) => `1px solid ${dayIndex === 1 ? t.palette.grey[600] : t.palette.grey[400]}`,
                cursor: 'pointer',
                bgcolor: isToday ? (t) => `${t.palette.info.light}20` : undefined,
                ':hover': {
                  bgcolor: (t) => (isToday ? `${t.palette.info.light}30` : `${t.palette.primary.light}10`),
                },
              }}
            />
          );
        })}
        {shifts.map((shift) => (
          <ShiftItem
            key={shift.id}
            shift={shift}
            start={start}
            end={end}
            onShiftEdit={onShiftEdit}
            onDuplicate={onDuplicate}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ShiftLine;
